import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Lock, Mail, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { usePlatformAuth } from "@/contexts/platform-auth-context";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";

export const Route = createFileRoute("/master/login")({
  component: MasterLoginPage,
  head: () => ({ meta: [{ title: "Super Master — GPS Drive" }] }),
});

function MasterLoginPage() {
  const navigate = useNavigate();
  const { status, login } = usePlatformAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Já logado (sessão recuperada via cookie) — pula direto pra lista de
  // empresas em vez de mostrar o formulário de novo.
  useEffect(() => {
    if (status === "ok") navigate({ to: "/master", replace: true });
  }, [status, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Lê o valor direto do DOM em vez de confiar só no state — o
      // autofill do Chrome/Google Password Manager às vezes preenche o
      // campo sem disparar o onChange do React (o state fica "" enquanto o
      // input mostra preenchido), e o usuário digitando por cima pra
      // corrigir pode não sincronizar de volta a tempo do clique em
      // "Entrar". O DOM é sempre a fonte da verdade do que a pessoa vê.
      const emailValue = emailRef.current?.value ?? email;
      const passwordValue = passwordRef.current?.value ?? password;
      await login(emailValue, passwordValue);
      navigate({ to: "/master" });
    } catch (err) {
      setError(
        err instanceof PlatformApiError && err.status === 401
          ? "Credenciais inválidas."
          : "Não foi possível entrar. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-slate-950 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-500/10">
            <ShieldCheck className="h-6 w-6 text-sky-400" />
          </div>
          <h1 className="mt-3 text-xl font-bold text-white">Super Master</h1>
          <p className="mt-1 text-sm text-slate-400">Gestão das empresas da plataforma GPS Drive</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-300">E-mail</label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                ref={emailRef}
                type="email"
                name="master-admin-email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@gpsdrive.com.br"
                className="h-11 border-slate-700 bg-slate-800 pl-9 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-300">Senha</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                ref={passwordRef}
                type={showPass ? "text" : "password"}
                name="master-admin-password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 border-slate-700 bg-slate-800 pl-9 pr-10 text-white placeholder:text-slate-500"
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                aria-label={showPass ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <p role="alert" className="text-sm font-medium text-red-400">
              {error}
            </p>
          )}

          <Button type="submit" disabled={loading} className="h-11 w-full bg-sky-500 text-white hover:bg-sky-400">
            {loading ? "Entrando…" : "Entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
}
